import React, {
    useContext,
    useState,
    useEffect,
    useRef,
    JSX,
    RefObject,
    ChangeEvent,
    ClassAttributes,
    HTMLAttributes
} from 'react';
import { WorkflowContext } from "../../../context/WorkflowContextProvider";
import '../../../styles/tester.scss';
import CallEndIcon from '@mui/icons-material/CallEnd';

import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {dark} from 'react-syntax-highlighter/dist/esm/styles/prism';

import { DialOperator } from "./DialOperator";
import {CircularProgress} from "@mui/material";
import DOMPurify from "dompurify";

function generateImageIcon(icon: any): JSX.Element {
    let path: string = '';
    let alt: string = '';

    if (icon) {
        switch (icon) {
            case "looking":
                path = "/images/thinking/eyes.png";
                alt = "looking";
                break;
            case 'success':
                path = "/images/thinking/check.png";
                alt = "success";
                break;
            case 'error':
                path = "/images/thinking/error.png";
                alt = "error";
                break;
            case 'thinking':
                path = "/images/thinking/thinking.gif";
                alt = "thinking";
                break;
            case 'processing':
                path = "/images/thinking/processing.png";
                alt = "processing";
                break;
            case 'planning':
                path = "/images/thinking/planning.png";
                alt = "planning";
                break;
            case 'researching':
                path = "/images/thinking/research.png";
                alt = "researching";
                break;
            case 'reviewing':
                path = "/images/thinking/reviewing.png";
                alt = "reviewing";
                break;
            case 'analyse':
                path = "/images/thinking/analyse.png";
                alt = "analyse";
                break;
            case 'writing':
                path = "/images/thinking/writing.png";
                alt = "writing";
                break;

        }
    }
    if (path !== '') {
        return <img src={ path }
                    alt={ alt }
                    style={ {
                        width: '30px',
                        margin: '0 20px 0 0',
                        position: 'relative',
                        top: '8px'
                    } } />
    }
    return <></>
}


const MarkDownComponent: (props: { text: string }) => JSX.Element = (props: { text: string }): JSX.Element => {
    function LinkRenderer(props: any): JSX.Element {
        return (
            <a href={ props.href } target="_blank" rel="noreferrer">
                { props.children }
            </a>
        );
    }

    return (
        <Markdown
            components={{
                a: LinkRenderer,
                code({node, className, children, ...props}: ClassAttributes<any> & HTMLAttributes<any> & any ): JSX.Element {
                    const match: RegExpExecArray = /language-(\w+)/.exec(className || '')
                    return match ? (
                        <SyntaxHighlighter
                            children={String(children).replace(/\n$/, '')}
                            style={dark}
                            language={match[1]}
                            PreTag="div"
                            {...props}
                        />
                    ) : (
                        <code className={ className } {...props}>
                            { children }
                        </code>
                    )
                }
            }}
            remarkPlugins={[remarkGfm]}>
            { props?.text }
        </Markdown>
    );
}

export const ChatWindow: () => JSX.Element = (): JSX.Element => {
    const context: any = useContext(WorkflowContext);
    const [messages, setMessages] = useState<string>();
    const [sendingConditional, setSendingConditional] = useState<boolean>(false);
    const chatMessageRef: RefObject<any> = useRef(null);

    useEffect( (): void => {
        let container: any = chatMessageRef.current;
        if (container) {
            container.scrollTop = container.scrollHeight;
        }

    }, [context.activeChatMessages, context.activeTestMessages] );

    if (context.operator === undefined) {
        return (
            <DialOperator />
        );
    }

    function handleOperatorMessage(event: any): void {
        setMessages(event.target.value);
    }

    function handleOperatorHangUp(): void {
        context.disconnectOperator();
    }

    function sendMessageToOperator(value: string = undefined): void {
        if (context.testOperatorThinking === false) {
            let msg: string = '';
            if (messages !== undefined) {
                msg = messages;
            }
            if (value !== undefined) {
                msg = value;
            }
            context.sendMessageToOperator(msg, true, true );
            setMessages('');
        }
    }

    function handleShowErrorsClick(): void {
        context.toggleShowErrorMessagesPanel(!context.showErrorMessagesPanel);
    }

    function handleConditionalClick(type: string): void {
        sendMessageToOperator(type);
    }

    function cleanHtmlContent(emailContent: string): string {
        return DOMPurify.sanitize(
            emailContent,
            {
                WHOLE_DOCUMENT: true,
                ADD_TAGS: ['head', 'meta', 'body', 'title', 'DOCTYPE']
            }
        );
    }

    return (
        <div className="testing__chat">
            <div className="chat__window" ref={chatMessageRef}>

                { (context.showErrorMessagesPanel === true) &&
                    <>
                        { context.errorMessages.map( (message: any, index: number): JSX.Element => {
                            return (
                                <div className={ "window__message error" } key={ "error_message_" + index }>
                                    <div className={ "message__wrapper" }>
                                        <pre className="message" >
                                            { message.message }
                                        </pre>
                                    </div>
                                </div>
                            );
                        }) }
                    </>
                }

                { (context.showErrorMessagesPanel === false) &&
                    <>
                        { context.activeTestMessages.map( (message: any, index: number): JSX.Element => {

                            if (message.type === 'email_draft') {
                                return (
                                    <div style={ { width: '100%', border: '2px solid', borderColor: 'divider', padding: '10px', borderRadius: '15px'  } } key={ "message_" + index }>
                                        <div style={ { width: '100%', margin: '0 0 10px 0', padding: '0 0 10px 0', borderBottom: '2px solid', borderBottomColor: 'divider' } }>
                                            <span>{ message?.data?.subject }</span>
                                        </div>
                                        <div style={ { width: '100%', padding: '2px', overflow: 'hidden' } }
                                             dangerouslySetInnerHTML={ {
                                                 __html: cleanHtmlContent(message?.data?.body)
                                             } } />
                                        {/*<MarkDownComponent text={ message?.data?.body } />*/}
                                        {/*<p>{ message?.data?.body }</p>*/}
                                    </div>
                                );
                            }
                            if (message.type === 'operator_tool_output') {
                                return (
                                    <div className={ "window__message testing left" } key={ "message_" + index }>
                                        <div className={ "message__wrapper tool " + message?.data?.data?.data_type } >
                                            { (message?.data?.data?.data_type === 'image') &&
                                                <>
                                                    { (!message?.data?.data?.value) &&
                                                        <div className="message__loading-image">
                                                            <span>{ "Loading image..." }</span>
                                                            <CircularProgress />
                                                        </div>
                                                    }
                                                    { (message?.data?.data?.value) &&
                                                        <img src={message?.data?.data?.value} alt="" />
                                                    }
                                                </>
                                            }
                                            { (message?.data?.data?.data_type === 'screenshot') &&
                                                <>
                                                    { (!message?.data?.data?.value) &&
                                                        <div className="message__loading-image">
                                                            <span>{ "Loading image..." }</span>
                                                            <CircularProgress />
                                                        </div>
                                                    }
                                                    { (message?.data?.data?.value) &&
                                                        <div className="message__screenshot">
                                                            <span>{ message?.data?.data?.title }</span>
                                                            <img src={message?.data?.data?.value} alt="" />
                                                        </div>
                                                    }
                                                </>
                                            }
                                        </div>

                                    </div>
                                );
                            }

                            // const isOperator: boolean = (message?.author?.toLowerCase() === 'operator' || message?.author.toLowerCase() === 'system');
                            if (typeof message?.message !== 'string') {
                                return <div key={ index }></div>
                            }
                            return (
                                <div className={ "window__message testing" + (message.token === ' operator' ? ' left' : ' right') } key={ "message_" + index }>
                                    <div className={ "message__wrapper" }>
                                        {/*{ (isOperator === false && message?.author ) &&*/}
                                        {/*    <span className="author">{ message.author }</span>*/}
                                        {/*}*/}

                                        <MarkDownComponent text={ message.message } />
                                    </div>
                                </div>
                            );
                        }) }

                    </>
                }

                { (context?.activeStatus && context.testOperatorThinking === true) &&
                    <div style={ { width: '100%' } } >
                        <div style={ { width: '100%', display: 'flex' } } >
                            { (context?.activeStatus?.data_type === 'text') &&
                                <pre>
                                            { generateImageIcon(context?.activeStatus?.icon) }
                                    { context?.activeStatus?.value }
                                        </pre>
                            }
                            { (context?.activeStatus?.data_type === 'thinking') &&
                                <>
                                    <img src="/images/thinking/thinking.gif" alt="thinking" />
                                    <span>{ "Thinking..." }</span>
                                </>
                            }
                        </div>

                    </div>
                }

                {/*{ (context.testOperatorThinking === true) &&*/}
                {/*    <div className="thinking">*/}
                {/*        <div className="thinking__thinking">*/}
                {/*            <div className="typing">*/}
                {/*                <div className="typing__dot"></div>*/}
                {/*                <div className="typing__dot"></div>*/}
                {/*                <div className="typing__dot"></div>*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*    </div>*/}
                {/*}*/}
            </div>
            <div className="chat__form">
                <div className="form__input">
                    { (context.conditionalInterrupt === false) &&
                        <>
                            <div className="input__mode">
                                <div className="mode__toggle">
                                    <span className="toggle__errors" onClick={ handleShowErrorsClick }>
                                        { (context.showErrorMessagesPanel === true) &&
                                            <>
                                                { "View Output" }
                                            </>
                                        }
                                        { (context.showErrorMessagesPanel === false) &&
                                            <>
                                                { "View Errors" }
                                            </>
                                        }
                                    </span>
                                </div>
                                <span className="mode__description">{ "Your workflow will be executed by the Operator" }</span>
                            </div>
                            <textarea
                                className={ ( context.interruptWaiting ? '  interruptWaiting' : '' ) }
                                value={ messages }
                                onChange={ (e: ChangeEvent): void => handleOperatorMessage(e) }
                                disabled={ context.testOperatorThinking }
                            />
                        </>
                    }

                    { (context.conditionalInterrupt === true) &&
                        <div className="input__conditional-options">
                            <div className="conditional-options__item continue">
                                <button
                                    disabled={ sendingConditional }
                                    onClick={ (): void => handleConditionalClick('continue') } >
                                    { (sendingConditional === false) &&
                                        <span>{ "Continue" }</span>
                                    }
                                    { (sendingConditional === true) &&
                                        <CircularProgress />
                                    }
                                </button>
                            </div>
                            <div className="conditional-options__item fail">
                                <button
                                    disabled={ sendingConditional }
                                    onClick={ (): void => handleConditionalClick('false') } >
                                    { (sendingConditional === false) &&
                                        <span>{ "Retry" }</span>
                                    }
                                    { (sendingConditional === true) &&
                                        <CircularProgress />
                                    }
                                </button>
                            </div>
                        </div>
                    }
                </div>
                { (context.conditionalInterrupt === false) &&
                    <div className="form__actions">
                        <span className="hangup" onClick={ handleOperatorHangUp }>
                            <CallEndIcon />
                        </span>
                        <span className="send test" onClick={ (): void => sendMessageToOperator() }>
                            { "Test Workflow" }
                        </span>
                    </div>
                }
            </div>
        </div>
    );
}